import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  fetchNotificationSettings,
  fetchNotifications,
  readAllNotifications,
  readNotification,
  saveNotificationSettings,
  saveNotificationSound,
} from './api'
import { DEFAULT_NOTIFICATION_SOUND_KEY, NOTIFICATION_SOUNDS } from './sound-options'
import {
  NotificationItem,
  NotificationSetting,
  NotificationSoundKey,
  NotificationSoundOption,
  NotificationStatus,
} from './types'

interface NotificationContextValue {
  items: NotificationItem[]
  unreadCount: number
  hasMore: boolean
  loading: boolean
  settingsLoading: boolean
  settingsLoaded: boolean
  hasRoleNotificationAccess: boolean
  activeStatus: NotificationStatus
  settings: NotificationSetting[]
  soundKey: NotificationSoundKey
  soundOptions: NotificationSoundOption[]
  loadNotifications: (status?: NotificationStatus, page?: number, append?: boolean) => Promise<void>
  loadSettings: () => Promise<NotificationSetting[]>
  addRealtimeNotification: (item: NotificationItem) => void
  markAsRead: (id: number) => Promise<NotificationItem | null>
  markAllAsRead: () => Promise<void>
  updateSettings: (settings: NotificationSetting[]) => Promise<void>
  updateSound: (soundKey: NotificationSoundKey) => Promise<void>
}

const NotificationContext = createContext<NotificationContextValue | null>(null)
const PAGE_SIZE = 20

export const NotificationProvider = ({ children }: { children: ReactNode }) => {
  const [items, setItems] = useState<NotificationItem[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(false)
  const [settingsLoading, setSettingsLoading] = useState(false)
  const [settingsLoaded, setSettingsLoaded] = useState(false)
  const [activeStatus, setActiveStatus] = useState<NotificationStatus>('all')
  const [settings, setSettings] = useState<NotificationSetting[]>([])
  const [soundKey, setSoundKey] = useState<NotificationSoundKey>(DEFAULT_NOTIFICATION_SOUND_KEY)
  const [soundOptions, setSoundOptions] = useState<NotificationSoundOption[]>(
    NOTIFICATION_SOUNDS.map(({ key, label, description }) => ({ key, label, description })),
  )
  const soundUpdateRequestRef = useRef(0)
  const hasRoleNotificationAccess = useMemo(
    () => settings.some(item => item.role_enabled),
    [settings],
  )

  const loadNotifications = useCallback(
    async (status: NotificationStatus = 'all', page = 1, append = false) => {
      setLoading(true)
      try {
        const response = await fetchNotifications(status, page, PAGE_SIZE)
        setActiveStatus(status)
        setUnreadCount(response.unread_count)
        setHasMore(response.has_more)
        setItems(prev => (append ? [...prev, ...response.items] : response.items))
      } finally {
        setLoading(false)
      }
    },
    [],
  )

  const loadSettings = useCallback(async () => {
    setSettingsLoading(true)
    try {
      const response = await fetchNotificationSettings()
      const nextSettings = response.settings || []
      setSettings(nextSettings)
      setSoundKey(response.sound_key || DEFAULT_NOTIFICATION_SOUND_KEY)
      if (response.sounds?.length) {
        setSoundOptions(response.sounds)
      }
      setSettingsLoaded(true)
      return nextSettings
    } finally {
      setSettingsLoading(false)
    }
  }, [])

  const addRealtimeNotification = useCallback(
    (item: NotificationItem) => {
      setUnreadCount(prev => (item.read_at ? prev : prev + 1))
      setItems(prev => {
        if (prev.some(current => current.id === item.id)) {
          return prev
        }
        if (activeStatus === 'unread' && item.read_at) {
          return prev
        }
        return [item, ...prev].slice(0, PAGE_SIZE)
      })
    },
    [activeStatus],
  )

  const markAsRead = useCallback(
    async (id: number) => {
      let wasUnread = false
      const previousItems = items
      const previousUnreadCount = unreadCount
      setItems(prev =>
        prev.flatMap(item => {
          if (item.id !== id || item.read_at) {
            return [item]
          }
          wasUnread = true
          if (activeStatus === 'unread') {
            return []
          }
          return [{ ...item, read_at: new Date().toISOString() }]
        }),
      )
      if (wasUnread) {
        setUnreadCount(prev => Math.max(prev - 1, 0))
      }

      try {
        const response = await readNotification(id)
        setItems(prev => prev.map(item => (item.id === id ? { ...item, ...response.item } : item)))
        return response.item
      } catch (err) {
        if (wasUnread) {
          setUnreadCount(previousUnreadCount)
          setItems(previousItems)
        }
        throw err
      }
    },
    [activeStatus, items, unreadCount],
  )

  const markAllAsRead = useCallback(async () => {
    const previousItems = items
    const previousUnreadCount = unreadCount
    const readAt = new Date().toISOString()
    setUnreadCount(0)
    setItems(prev =>
      activeStatus === 'unread'
        ? []
        : prev.map(item => ({ ...item, read_at: item.read_at || readAt })),
    )

    try {
      await readAllNotifications()
    } catch (err) {
      setItems(previousItems)
      setUnreadCount(previousUnreadCount)
      throw err
    }
  }, [activeStatus, items, unreadCount])

  const updateSettings = useCallback(
    async (nextSettings: NotificationSetting[]) => {
      const previous = settings
      setSettings(nextSettings)
      try {
        const response = await saveNotificationSettings(nextSettings)
        setSettings(response.settings || [])
      } catch (err) {
        setSettings(previous)
        throw err
      }
    },
    [settings],
  )

  const updateSound = useCallback(
    async (nextSoundKey: NotificationSoundKey) => {
      const requestId = ++soundUpdateRequestRef.current
      const previous = soundKey
      setSoundKey(nextSoundKey)
      try {
        const response = await saveNotificationSound(nextSoundKey)
        if (requestId === soundUpdateRequestRef.current) {
          setSoundKey(response.sound_key || DEFAULT_NOTIFICATION_SOUND_KEY)
        }
      } catch (err) {
        if (requestId === soundUpdateRequestRef.current) {
          setSoundKey(previous)
        }
        throw err
      }
    },
    [soundKey],
  )

  useEffect(() => {
    let isMounted = true

    const initialize = async () => {
      try {
        const nextSettings = await loadSettings()
        if (!isMounted) {
          return
        }

        if (nextSettings.some(item => item.role_enabled)) {
          await loadNotifications('all')
          return
        }

        setItems([])
        setUnreadCount(0)
        setHasMore(false)
      } catch (err) {
        console.error('[Notifications] Initialize error:', err)
      }
    }

    initialize()

    return () => {
      isMounted = false
    }
  }, [loadNotifications, loadSettings])

  const value = useMemo(
    () => ({
      items,
      unreadCount,
      hasMore,
      loading,
      settingsLoading,
      settingsLoaded,
      hasRoleNotificationAccess,
      activeStatus,
      settings,
      soundKey,
      soundOptions,
      loadNotifications,
      loadSettings,
      addRealtimeNotification,
      markAsRead,
      markAllAsRead,
      updateSettings,
      updateSound,
    }),
    [
      items,
      unreadCount,
      hasMore,
      loading,
      settingsLoading,
      settingsLoaded,
      hasRoleNotificationAccess,
      activeStatus,
      settings,
      soundKey,
      soundOptions,
      loadNotifications,
      loadSettings,
      addRealtimeNotification,
      markAsRead,
      markAllAsRead,
      updateSettings,
      updateSound,
    ],
  )

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>
}

export const useNotifications = () => {
  const context = useContext(NotificationContext)
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider')
  }
  return context
}
